<template>
    <!-- While the save is in flight the field gives way to a spinner, so the stale value never
         shows next to the new one; the Inertia reload brings the saved value back through props. -->
    <div v-if="isSaving" class="profile-editable-field">
        <i v-if="field === 'job_title'" class="bi bi-briefcase text-base/none shrink-0" aria-hidden="true" />
        <LoadingSpinner class="size-6 shrink-0" />
    </div>
    <BaseEditableField
        v-else
        :model-value="value ?? ''"
        :label="fieldConfig.label"
        :placeholder="placeholder ?? fieldConfig.placeholder"
        submit-mode="blur"
        size="compact"
        required
        :data-testid="fieldConfig.testIdPrefix"
        @submit="handleSubmit">
        <template v-if="field === 'job_title'" #prefix>
            <i class="bi bi-briefcase" aria-hidden="true" />
        </template>
    </BaseEditableField>
</template>

<script setup lang="ts">
import { getCookie } from "/js/utils.js";
import { router } from "@inertiajs/vue3";
import BaseEditableField from "~vue/components/form/BaseEditableField.vue";
import LoadingSpinner from "~vue/icons/LoadingSpinner.vue";
import type { UserProfileEndpoints } from "~vue/UserProfile/types";
import { computed, ref } from "vue";
import { useI18n } from "vue-i18n";

const { field, value, endpoints, placeholder } = defineProps<{
    field: "full_name" | "job_title";
    value: string | null;
    endpoints: UserProfileEndpoints;
    placeholder?: string;
}>();

const { t } = useI18n();

// The label names the control and is the stem of the edit, save and cancel button names.
const fieldConfig = computed(() =>
    field === "full_name"
        ? {
              label: t("profile.editableField.fullName.inputA11yLabel"),
              testIdPrefix: "profile-name",
              refreshProp: "user_name",
              placeholder: t("profile.editableField.fullName.placeholder"),
          }
        : { label: t("profile.editableField.jobTitle.inputA11yLabel"), testIdPrefix: "profile-job-title", refreshProp: "user_profile", placeholder: "" },
);

const isSaving = ref(false);
const handleSubmit = (newValue: string) => {
    // Leaving the field in blur mode submits whatever is there, so an opened field left
    // as it was would otherwise post the same value and bump the profile's updated_at.
    if (!newValue?.trim() || newValue.trim() === (value ?? "").trim()) {
        return;
    }

    router.post(
        endpoints.profile,
        { detail: field, [field]: newValue },
        {
            headers: {
                "X-CSRFToken": getCookie("csrftoken") ?? "",
                "X-Inertia-Next": endpoints.current_page,
            },
            preserveScroll: true,
            only: [fieldConfig.value.refreshProp, "errors"],
            onStart: () => (isSaving.value = true),
            onFinish: () => (isSaving.value = false),
        },
    );
};
</script>

<style scoped>
@reference "../../../css/tailwind.css";

@layer components {
    .profile-editable-field {
        @apply inline-flex min-w-0 max-w-full items-center gap-2;
    }
}
</style>
